import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { adminApi, Card, Badge } from './_shared';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const ACTIONS = ['view', 'create', 'edit', 'delete', 'approve'];

export default function AdminRoleTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [editPerms, setEditPerms] = useState({});

  const load = async () => { setLoading(true); try { const r = await adminApi.roles(); setTemplates(r.templates || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const open = (t) => { setActive(t); setEditPerms(JSON.parse(JSON.stringify(t.modulePermissions || {}))); };
  const togglePerm = (mod, action) => {
    setEditPerms(p => ({ ...p, [mod]: { ...(p[mod] || {}), [action]: !(p[mod]?.[action]) } }));
  };
  const save = async () => {
    try { await adminApi.updateRole(active.roleCode, { modulePermissions: editPerms }); toast.success('Saved'); setActive(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const moduleCodes = active ? Array.from(new Set([...Object.keys(active.modulePermissions || {}), ...Object.keys(editPerms)])).sort() : [];

  return (
    <AdminLayout title="Role Templates" subtitle="Admin · Permissions Defaults">
      <p className="text-sm text-slate-400 mb-6 max-w-3xl">
        Edit the default permission matrix for each role. Existing project memberships keep their snapshotted permissions —
        new memberships will use the updated template values.
      </p>
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-500"/></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="roles-grid">
          {templates.map(t => {
            const total = Object.keys(t.modulePermissions || {}).length;
            return (
              <Card key={t.roleCode} className="p-5 cursor-pointer hover:border-[#1E4E8C] transition" onClick={() => open(t)} data-testid={`role-${t.roleCode}`}>
                <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase mb-1">{t.roleCategory || 'Project Role'}</div>
                <h3 className="text-base text-slate-100 font-medium">{t.roleName || t.roleCode.replace(/_/g, ' ')}</h3>
                <div className="text-[11px] text-slate-500 font-mono mt-1">{t.roleCode}</div>
                <div className="mt-4 flex items-center gap-2">
                  <Badge tone="blue">{total} modules configured</Badge>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit drawer */}
      {active && (
        <div className="fixed inset-0 z-40 bg-black/60 flex justify-end" onClick={() => setActive(null)}>
          <div className="bg-[#0B1220] border-l border-slate-800 w-full max-w-3xl h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Editing</div>
                <h2 className="text-lg text-slate-100 font-medium">{active.roleName || active.roleCode}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setActive(null)} className="text-xs text-slate-500 hover:text-slate-100 px-3 py-2">Cancel</button>
                <button onClick={save} data-testid="role-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] uppercase tracking-wider bg-[#1E4E8C] hover:bg-[#143A6B] text-white"><Save className="w-3.5 h-3.5"/>Save Template</button>
              </div>
            </div>
            <div className="p-6">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/40">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500">Module</th>
                    {ACTIONS.map(a => (
                      <th key={a} className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500">{a}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {moduleCodes.map(mod => (
                    <tr key={mod} className="border-b border-slate-800/40">
                      <td className="px-3 py-2 text-slate-300 font-mono text-xs">{mod}</td>
                      {ACTIONS.map(a => (
                        <td key={a} className="px-3 py-2 text-center">
                          <input type="checkbox" checked={!!editPerms[mod]?.[a]} onChange={() => togglePerm(mod, a)} className="accent-[#1E4E8C]"/>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
